import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import {
  resendMfaLoginCode,
  startMfaChallenge,
  verifyMfaLoginCode,
  confirmMfaEnrollment,
} from "../../services/auth.service";
import { getMessage } from "../../utils/labelsMessage";

type MfaChannel = "email" | "sms";

interface MfaChallengeModalProps {
  isOpen: boolean;
  challengeId: string;
  // Which channels the backend says are actually available for this
  // account — "sms" is only ever present when a phone number is on file.
  availableChannels: MfaChannel[];
  // "login": account already has MFA on, this is an ordinary code check.
  // "enroll": account is being forced to turn MFA on to finish this login
  // (grace logins exhausted) — same code-entry mechanics, different framing,
  // and confirms via a different endpoint (no password anywhere in either).
  mode: "login" | "enroll";
  rememberMe?: boolean;
  onClose: () => void;
  onVerified: (data: any) => void;
}

const CODE_LENGTH = 6;

// Two steps: pick a channel (nothing sent yet), then enter the code once
// something's actually been sent. Unlike AccountTypeModal (purely
// presentational — signIn/API calls live in LoginForm), this modal owns its
// own start/verify/resend calls and all retry/error state; only the final
// verified result is handed back via onVerified.
const MfaChallengeModal: React.FC<MfaChallengeModalProps> = ({
  isOpen,
  challengeId,
  availableChannels,
  mode,
  rememberMe,
  onClose,
  onVerified,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<"channel" | "code">("channel");
  const [selectedChannel, setSelectedChannel] = useState<MfaChannel>("email");
  const [isStartingSend, setIsStartingSend] = useState(false);
  const [channelError, setChannelError] = useState<string | undefined>(undefined);
  const [sentVia, setSentVia] = useState<MfaChannel | null>(null);

  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [attemptsRemaining, setAttemptsRemaining] = useState<
    number | undefined
  >(undefined);
  const [isInvalidated, setIsInvalidated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isResendExhausted, setIsResendExhausted] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | undefined>(
    undefined,
  );
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  // Guards the auto-submit below against firing twice for the same six
  // digits. Re-armed the moment the code drops below full length, so a
  // rejected code can be retyped and re-submitted.
  const hasAutoSubmittedRef = useRef(false);

  // Reset everything, back to the channel-choice step, whenever a fresh
  // challenge is opened.
  useEffect(() => {
    if (!isOpen) return;
    hasAutoSubmittedRef.current = false;
    setStep("channel");
    setSelectedChannel("email");
    setIsStartingSend(false);
    setChannelError(undefined);
    setSentVia(null);
    setCode("");
    setTrustDevice(false);
    setError(undefined);
    setAttemptsRemaining(undefined);
    setIsInvalidated(false);
    setIsResendExhausted(false);
    setResendMessage(undefined);
    setCooldownRemaining(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, challengeId]);

  useEffect(() => {
    if (!isOpen || step !== "code" || cooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      setCooldownRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen, step, cooldownRemaining]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Declared up here with the other hooks (rather than beside the remaining
  // handlers below) because the auto-submit effect needs it and hooks can't
  // live past the `if (!isOpen) return null` guard.
  const submitCode = useCallback(async () => {
    if (code.length !== CODE_LENGTH || isVerifying || isInvalidated) return;

    setIsVerifying(true);
    setError(undefined);
    try {
      const confirmFn = mode === "enroll" ? confirmMfaEnrollment : verifyMfaLoginCode;
      const res = await confirmFn(challengeId, code, rememberMe, trustDevice);
      if (res?.ok) {
        onVerified(res.data);
        return;
      }
      setAttemptsRemaining(res?.attemptsRemaining);
      setError(getMessage(res?.errorCode as string) || "That code isn't right. Please try again.");
      // The specific reasons below always kill the challenge; attemptsRemaining
      // reaching 0 is the one signal the backend contract otherwise guarantees
      // on every failure, so it's checked too as a fallback in case the error
      // code itself ever changes.
      if (
        res?.errorCode === "mfa_challenge_invalidated" ||
        res?.errorCode === "mfa_challenge_not_found" ||
        res?.errorCode === "mfa_challenge_consumed" ||
        res?.errorCode === "mfa_code_expired" ||
        res?.attemptsRemaining === 0
      ) {
        setIsInvalidated(true);
      }
      setCode("");
    } finally {
      setIsVerifying(false);
    }
  }, [
    challengeId,
    code,
    isInvalidated,
    isVerifying,
    mode,
    onVerified,
    rememberMe,
    trustDevice,
  ]);

  // Submit as soon as the sixth digit lands, so nobody has to reach for
  // "Verify" — the behaviour people expect from an OTP field, and the reason
  // it matters here: iOS/Android one-time-code autofill drops all six digits
  // in at once, at which point an extra tap is pure friction. The button
  // stays as a fallback for the case where a submit fails and the same code
  // is still on screen.
  useEffect(() => {
    if (code.length !== CODE_LENGTH) {
      hasAutoSubmittedRef.current = false;
      return;
    }
    if (hasAutoSubmittedRef.current || isVerifying || isInvalidated) return;
    hasAutoSubmittedRef.current = true;
    void submitCode();
  }, [code, isVerifying, isInvalidated, submitCode]);

  if (!isOpen) return null;

  const isBusy = isStartingSend || isVerifying || isResending;

  const handleChannelSelect = async (channel: MfaChannel) => {
    if (isBusy) return;
    setIsStartingSend(true);
    setChannelError(undefined);
    try {
      const res = await startMfaChallenge(challengeId, channel);
      if (res?.ok) {
        setSentVia(res.channel || channel);
        setCooldownRemaining(res.resendCooldownSeconds ?? 60);
        setStep("code");
      } else {
        setChannelError(
          getMessage(res?.errorCode as string) ||
            "Couldn't send the code. Please try again or choose another option.",
        );
      }
    } finally {
      setIsStartingSend(false);
    }
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digitsOnly);
    setError(undefined);
  };

  // Enter, or the Verify button. Auto-submit (above) handles the normal case;
  // this covers a retry after a failed submit left the code on screen.
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submitCode();
  };

  const handleResend = async () => {
    if (isResending || cooldownRemaining > 0 || isInvalidated || isResendExhausted) return;
    setIsResending(true);
    setError(undefined);
    setResendMessage(undefined);
    try {
      const res = await resendMfaLoginCode(challengeId);
      if (res?.ok) {
        const via = res.channel || sentVia;
        setResendMessage(
          via === "sms"
            ? "A new code was sent to your phone."
            : "A new code was sent to your email.",
        );
        setCooldownRemaining(60);
      } else {
        setError(getMessage(res?.errorCode as string) || "Couldn't resend the code.");
        // The resend cap is independent of the 5-wrong-attempts limit (see
        // spec's OTP policy) — running out of resends still leaves whatever
        // code was last sent valid, so only the resend control gets disabled,
        // not the whole challenge.
        if (res?.errorCode === "mfa_resend_limit_reached") {
          setIsResendExhausted(true);
        } else if (
          res?.errorCode === "mfa_challenge_invalidated" ||
          res?.errorCode === "mfa_challenge_not_found" ||
          res?.errorCode === "mfa_challenge_consumed"
        ) {
          setIsInvalidated(true);
        }
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 h-screen py-3 px-3">
      <div
        ref={modalRef}
        className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-lg text-secondary font-semibold">
            Almost there 🙂! Please continue with Two-Factor Authentication to
            Login Securely
          </h2>
          <button
            onClick={onClose}
            className="text-secondary hover:text-red-500 transition-colors disabled:opacity-50 shrink-0"
            aria-label="Close"
            disabled={isBusy}
          >
            <IoCloseOutline size={28} />
          </button>
        </div>

        {mode === "enroll" && step === "channel" && (
          <p className="text-sm text-secondaryVariant bg-secondaryVariantNatural rounded-lg p-3 mb-4">
            You've reached the number of logins allowed without multi-factor
            authentication turned on — confirm a code to turn it on and
            finish signing in.
          </p>
        )}

        {step === "channel" ? (
          <div>
            <p className="text-sm text-secondaryVariant mb-4">
              Choose how you'd like to receive your verification code.
            </p>
            <div className="flex flex-col gap-3 mb-4">
              <label
                htmlFor="mfa-channel-email"
                className={`flex items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
                  selectedChannel === "email"
                    ? "border-primary bg-primary/5"
                    : "border-secondaryVariant2 hover:border-secondaryVariant"
                } ${isBusy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="radio"
                  id="mfa-channel-email"
                  name="mfa-channel"
                  value="email"
                  checked={selectedChannel === "email"}
                  onChange={() => setSelectedChannel("email")}
                  disabled={isBusy}
                  className="mt-0.5 shrink-0 h-5 w-5 appearance-none rounded-full border-2 border-secondaryVariant2 bg-white checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 before:block before:h-3 before:w-3 before:rounded-full before:bg-transparent before:content-[''] checked:before:bg-primary flex items-center justify-center box-border"
                />
                <span>
                  <span className="block text-sm font-medium text-secondary">
                    Email me a code
                  </span>
                  <span className="block text-xs text-secondaryVariant">
                    Sent to the email on your account
                  </span>
                </span>
              </label>
              {availableChannels?.includes("sms") && (
                <label
                  htmlFor="mfa-channel-sms"
                  className={`flex items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
                    selectedChannel === "sms"
                      ? "border-primary bg-primary/5"
                      : "border-secondaryVariant2 hover:border-secondaryVariant"
                  } ${isBusy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <input
                    type="radio"
                    id="mfa-channel-sms"
                    name="mfa-channel"
                    value="sms"
                    checked={selectedChannel === "sms"}
                    onChange={() => setSelectedChannel("sms")}
                    disabled={isBusy}
                    className="mt-0.5 shrink-0 h-5 w-5 appearance-none rounded-full border-2 border-secondaryVariant2 bg-white checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 before:block before:h-3 before:w-3 before:rounded-full before:bg-transparent before:content-[''] checked:before:bg-primary flex items-center justify-center box-border"
                  />
                  <span>
                    <span className="block text-sm font-medium text-secondary">
                      Text me a code
                    </span>
                    <span className="block text-xs text-secondaryVariant">
                      Sent to the phone number on your account
                    </span>
                  </span>
                </label>
              )}
            </div>
            <button
              type="button"
              className="btnPrimary"
              onClick={() => handleChannelSelect(selectedChannel)}
              disabled={isBusy}
            >
              {isStartingSend ? "Sending..." : "Continue"}
            </button>
            {channelError && <p className="text-red-600 text-sm mt-3">{channelError}</p>}
          </div>
        ) : isInvalidated ? (
          <div>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button className="btnPrimary" type="button" onClick={onClose}>
              Back to login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="text-sm text-secondaryVariant mb-4">
              We {sentVia === "sms" ? "texted" : "emailed"} you a 6-digit code.
              Enter it below to {mode === "enroll" ? "turn on multi-factor authentication and finish logging in" : "finish logging in"}.
            </p>
            <div className="mb-4">
              <label className="text-sm text-secondaryVariant" htmlFor="mfa-code">
                Code
              </label>
              <input
                id="mfa-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                maxLength={CODE_LENGTH}
                className="px-3 py-3.5 rounded-lg border border-secondaryVariant w-full bg-white mt-2 tracking-[0.5em] text-center text-lg"
                value={code}
                onChange={handleCodeChange}
                autoFocus
              />
              {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
              {typeof attemptsRemaining === "number" && attemptsRemaining > 0 && (
                <p className="text-secondaryVariant text-xs mt-1">
                  {attemptsRemaining} attempt{attemptsRemaining === 1 ? "" : "s"} remaining
                </p>
              )}
            </div>

            {/* Deliberately a separate, plainer control from the password
                screen's "remember me" checkbox — this trusts the device for
                the MFA code step specifically, not the session. */}
            <div className="flex items-center gap-2 mb-6">
              <input
                id="trust-device"
                type="checkbox"
                checked={trustDevice}
                onChange={() => setTrustDevice((prev) => !prev)}
                className="h-4 w-4"
              />
              <label htmlFor="trust-device" className="text-sm text-secondaryVariant">
                Trust this device for 30 days
              </label>
            </div>

            <button
              className="btnPrimary"
              type="submit"
              disabled={code.length !== CODE_LENGTH || isVerifying}
            >
              {isVerifying ? "Verifying..." : "Verify"}
            </button>

            <div className="mt-4 text-center">
              {resendMessage && (
                <p className="text-secondaryVariant text-sm mb-2">{resendMessage}</p>
              )}
              <button
                type="button"
                className="text-sm text-statusColor disabled:opacity-50"
                onClick={handleResend}
                disabled={isResending || cooldownRemaining > 0 || isResendExhausted}
              >
                {isResending
                  ? "Resending..."
                  : isResendExhausted
                    ? "Resend limit reached"
                    : cooldownRemaining > 0
                      ? `Resend code (${cooldownRemaining}s)`
                      : "Resend code"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MfaChallengeModal;
